// WelcomeOverlay — full-viewport intro shown on every app entry.
//
// Mirrors `mockup-navigator-v3-light.html` lines 488–497 and 755–769:
//   logo + Caveat "ophelia" wordmark, "Hey Kim, welcome to ophelia!" tagline,
//   two buttons: primary "Let's dive in →" + ghost "Show me around".
//
// Dismissal is session-scoped — sessionStorage so a refresh re-shows the
// overlay (matches the mockup behaviour where it appears on every page load).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { setupTour, waitForTourPaper } from "../api/tour";
import { useTourStore } from "../state/tourStore";
import { trackEvent } from "../telemetry/useTelemetry";
import { toast } from "../lib/toast";

import "./WelcomeOverlay.css";

const SESSION_KEY = "ophelia.welcome.dismissed";

export function WelcomeOverlay() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(SESSION_KEY) !== "1";
  });
  const [closing, setClosing] = useState(false);
  const [tourLoading, setTourLoading] = useState(false);
  const [tourLoadingMsg, setTourLoadingMsg] = useState("");
  const startTour = useTourStore((s) => s.start);

  useEffect(() => {
    if (!visible) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        dismiss();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  function dismiss() {
    setClosing(true);
    window.sessionStorage.setItem(SESSION_KEY, "1");
    trackEvent("welcome.dismiss");
    // Match the mockup's 0.35s fade.
    window.setTimeout(() => setVisible(false), 350);
  }

  if (!visible) return null;

  return (
    <div
      className={"welcome-overlay" + (closing ? " welcome-overlay--closing" : "")}
      role="dialog"
      aria-modal="true"
      aria-label={t("welcome.aria_label")}
      tabIndex={-1}
      autoFocus
    >
      <div className="welcome-logo-row">
        <img src="/logo.svg" alt="" className="welcome-logo" />
        <span className="welcome-wordmark">ophelia</span>
      </div>
      <div className="welcome-tagline">{t("welcome.greeting")}</div>
      <div className="welcome-btns">
        <button
          type="button"
          className="welcome-btn welcome-btn--primary"
          onClick={dismiss}
        >
          {t("welcome.dive_in")}
        </button>
        <button
          type="button"
          className="welcome-btn welcome-btn--ghost"
          disabled={tourLoading}
          onClick={() => {
            trackEvent("welcome.tour");
            setTourLoading(true);
            setTourLoadingMsg(t("common.loading"));
            setupTour()
              .then(async ({ projectId, papers }) => {
                if (papers.inReview) {
                  setTourLoadingMsg(t("tour.setup_processing"));
                  await waitForTourPaper(papers.inReview);
                }
                dismiss();
                startTour(projectId, papers);
              })
              .catch(() => {
                toast(t("tour.setup_failed"));
              })
              .finally(() => {
                setTourLoading(false);
                setTourLoadingMsg("");
              });
          }}
        >
          {t("welcome.show_around")}
        </button>
      </div>
      <p className="welcome-loading-status" aria-live="polite">
        {tourLoadingMsg}
      </p>
    </div>
  );
}
